<template src="./paymentExplanation.html"></template>

<script lang="ts">
    import { defineComponent } from 'vue';
    import { getFormatedAmount, getFormatedDate } from '../../../services/helper';
    import { ExplanationPart } from '../../../models/PaymentExplanation';

    // Правило форматирования параметров шаблонов объяснения: параметры, оканчивающиеся на
    // amount/rate/price/total/base, — деньги в копейках; перечисленные по имени — ISO-даты.
    const AMOUNT_PARAM_NAME_SUFFIXES = ['amount', 'rate', 'price', 'total', 'base'];
    const DATE_PARAM_NAMES = ['start', 'end', 'date', 'periodstart', 'periodend', 'lastfrozenday'];

    export default defineComponent({
        name: 'PaymentExplanation',
        props: ["explanation", "currencySign"],
        methods: {
            formatParams: function (params: Record<string, string>): Record<string, string> {
                const result: Record<string, string> = {};

                for (const key of Object.keys(params)) {
                    const value = params[key];
                    const lowerKey = key.toLowerCase().replace(/\d+$/, '');

                    if (AMOUNT_PARAM_NAME_SUFFIXES.some(suffix => lowerKey.endsWith(suffix))) {
                        result[key] = `${getFormatedAmount(Number(value))}\u00A0${this.currencySign}`;
                    } else if (DATE_PARAM_NAMES.includes(lowerKey) || lowerKey.endsWith('date')) {
                        result[key] = getFormatedDate(value, 'DD.MM.yyyy') || value;
                    } else {
                        result[key] = value;
                    }
                }

                return result;
            },
            // Интерполяция выполняется самим vue-i18n; ФИО клиента при наличии clientId
            // и итоговая сумма {total} подменяются токенами, которые после
            // экранирования всего текста заменяются ссылкой и жирным начертанием
            renderPart: function (part: ExplanationPart): string {
                const formatted = this.formatParams(part.params);
                const linkedKeys = ['name', 'inviterName']
                    .filter(key => formatted[key] !== undefined && part.params.clientId);

                const interpolationParams: Record<string, string> = { ...formatted };

                for (const key of linkedKeys) {
                    interpolationParams[key] = `@@client_link_${key}@@`;
                }

                const hasTotal = formatted.total !== undefined;

                if (hasTotal) {
                    interpolationParams.total = '@@bold_total@@';
                }

                // Суммы разнонаправленных долгов подсвечиваются цветом по направлению
                // из ключа шаблона; нулевые суммы и админ-диагностика не красятся
                const amountClass = !part.templateKey.startsWith('order.explanation.') ? null
                    : part.templateKey.includes('club_owes') ? 'payment_explanation_amount_club_owes'
                    : part.templateKey.includes('client_owes') ? 'payment_explanation_amount_client_owes'
                    : null;

                const colorAmount = amountClass !== null
                    && formatted.amount !== undefined
                    && Number(part.params.amount) !== 0;

                if (colorAmount) {
                    interpolationParams.amount = '@@amount@@';
                }

                let html = this.escapeHtml(String(this.$t(part.templateKey, interpolationParams)));

                for (const key of linkedKeys) {
                    const clientId = Number(part.params.clientId);
                    const anchor = `<a class="payment_explanation_client_link" data-client-id="${clientId}" href="/client/${clientId}">${this.escapeHtml(formatted[key])}</a>`;

                    html = html.split(`@@client_link_${key}@@`).join(anchor);
                }

                if (hasTotal) {
                    html = html.split('@@bold_total@@').join(`<b>${this.escapeHtml(formatted.total)}</b>`);
                }

                if (colorAmount) {
                    html = html.split('@@amount@@').join(`<span class="${amountClass}">${this.escapeHtml(formatted.amount)}</span>`);
                }

                return html;
            },
            escapeHtml: function (value: string): string {
                return value
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;');
            },
            onExplanationClick: function (event: Event) {
                const link = (event.target as HTMLElement)?.closest?.('a[data-client-id]');

                if (!link) return;

                event.preventDefault();
                this.$router.push(`/client/${link.getAttribute('data-client-id')}`);
            }
        }
    });
</script>

<style scoped>
    .payment_explanation {
        margin-top: 12px;
    }

    .payment_explanation_item {
        margin-bottom: 12px;
    }

    .payment_explanation_summary {
        margin: 0px 0px 4px 0px;
        white-space: pre-line;
    }

    .payment_explanation_details {
        margin: 0px;
        padding-left: 20px;
        color: #65656F;
    }

    .payment_explanation_details li {
        margin-bottom: 2px;
    }

    .payment_explanation_total {
        margin: 0px 0px 12px 0px;
    }

    .payment_explanation_date {
        color: #65656F;
        margin: 0px 0px 12px 0px;
    }

    .payment_explanation_diagnostics {
        background-color: #ffe5e5;
        color: #a10000;
        border-radius: 12px;
        padding: 12px 16px;
    }

    .payment_explanation_diagnostics_header {
        margin: 0px 0px 8px 0px;
    }

    .payment_explanation_diagnostics ul {
        margin: 0px;
        padding-left: 20px;
    }

    .payment_explanation_diagnostics li {
        margin-bottom: 4px;
        white-space: pre-line;
    }

    :deep(.payment_explanation_client_link) {
        color: #21A7D1;
        text-decoration: underline;
        cursor: pointer;
    }

    :deep(.payment_explanation_amount_club_owes) {
        color: #1b7f3b;
    }

    :deep(.payment_explanation_amount_client_owes) {
        color: #8a5a00;
    }
</style>
